import { createUserWithEmailAndPassword } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth, functions } from "./firebase";
import type {
  BookingData,
  DayAvailability,
  DropoffAvailability,
  RepairCapacityDay,
  RepairRequestFormData,
  WebOrderData,
  WebOrderPickupAvailability,
} from "./types";
export type { RepairRequestFormData } from "./types";

const getBookingDataFn = httpsCallable<{ salonCode: string }, BookingData>(
  functions,
  "getBookingData"
);
const getDayAvailabilityFn = httpsCallable<
  { salonCode: string; date: string; employeeId?: string | null },
  DayAvailability
>(functions, "getDayAvailability");
const createWebBookingFn = httpsCallable(functions, "createWebBooking");
const setupWebCustomerFn = httpsCallable(functions, "setupWebCustomer");
const cancelWebBookingFn = httpsCallable(functions, "cancelWebBooking");
const getWebOrderFn = httpsCallable<
  { claimCode: string; email: string; locale?: string },
  WebOrderData
>(functions, "getWebOrder");
const getWebOrderPickupAvailabilityFn = httpsCallable<
  { claimCode: string; email: string; date: string; employeeId?: string },
  WebOrderPickupAvailability
>(functions, "getWebOrderPickupAvailability");
const scheduleWebOrderPickupFn = httpsCallable(functions, "scheduleWebOrderPickup");
const getWebOrderDropoffAvailabilityFn = httpsCallable<
  { claimCode: string; email: string; date: string; employeeId?: string },
  DropoffAvailability
>(functions, "getWebOrderDropoffAvailability");
const scheduleWebOrderDropoffFn = httpsCallable(functions, "scheduleWebOrderDropoff");
const cancelWebOrderDropoffFn = httpsCallable(functions, "cancelWebOrderDropoff");
const answerWebOrderQuestionFn = httpsCallable(functions, "answerWebOrderQuestion");
const addWebOrderNoteFn = httpsCallable(functions, "addWebOrderNote");
const addWebOrderAttachmentFn = httpsCallable(functions, "addWebOrderAttachment");

export async function fetchBookingData(salonCode: string): Promise<BookingData> {
  const result = await getBookingDataFn({ salonCode });
  return result.data;
}

export async function fetchDayAvailability(
  salonCode: string,
  date: string,
  employeeId?: string | null
): Promise<DayAvailability> {
  const result = await getDayAvailabilityFn({ salonCode, date, employeeId });
  return result.data;
}

export async function submitWebBooking(params: {
  salonCode: string;
  serviceId: string;
  employeeId?: string | null;
  noPreference?: boolean;
  startAt: string;
  guestName: string;
  guestPhone?: string | null;
  guestEmail?: string | null;
  note?: string | null;
  locale?: string;
}): Promise<{ appointmentId: string }> {
  const result = await createWebBookingFn(params);
  const data = result.data as { ok: boolean; appointmentId: string };
  return { appointmentId: data.appointmentId };
}

export async function registerWebCustomer(params: {
  salonCode: string;
  email: string;
  password: string;
  firstName: string;
  lastName?: string;
  phone?: string | null;
}): Promise<void> {
  const credential = await createUserWithEmailAndPassword(
    auth,
    params.email,
    params.password
  );
  const nameParts = params.firstName.trim().split(/\s+/);
  const firstName = nameParts[0] ?? params.firstName;
  const lastName =
    params.lastName?.trim() ||
    (nameParts.length > 1 ? nameParts.slice(1).join(" ") : "");
  await setupWebCustomerFn({
    salonCode: params.salonCode,
    firstName,
    lastName,
    phone: params.phone ?? null,
  });
  void credential;
}

export async function fetchWebOrder(params: {
  claimCode: string;
  email: string;
  locale?: string;
}): Promise<WebOrderData> {
  const result = await getWebOrderFn(params);
  return result.data;
}

export async function fetchWebOrderPickupAvailability(params: {
  claimCode: string;
  email: string;
  date: string;
  employeeId?: string;
}): Promise<WebOrderPickupAvailability> {
  const result = await getWebOrderPickupAvailabilityFn(params);
  return result.data;
}

export async function submitWebOrderPickup(params: {
  claimCode: string;
  email: string;
  employeeId: string;
  startAt: string;
}): Promise<{ pickupAppointment: { startAt: string; endAt: string } }> {
  const result = await scheduleWebOrderPickupFn(params);
  const data = result.data as {
    ok: boolean;
    pickupAppointment: { startAt: string; endAt: string };
  };
  return { pickupAppointment: data.pickupAppointment };
}

export async function fetchWebOrderDropoffAvailability(params: {
  claimCode: string;
  email: string;
  date: string;
  employeeId?: string;
}): Promise<DropoffAvailability> {
  const result = await getWebOrderDropoffAvailabilityFn(params);
  return result.data;
}

export async function scheduleWebOrderDropoff(params: {
  claimCode: string;
  email: string;
  employeeId: string;
  startAt: string;
  replace?: boolean;
  locale?: string;
}): Promise<{ dropoffAppointment: { startAt: string; endAt: string } }> {
  const result = await scheduleWebOrderDropoffFn(params);
  const data = result.data as {
    ok: boolean;
    dropoffAppointment: { startAt: string; endAt: string };
  };
  return { dropoffAppointment: data.dropoffAppointment };
}

export async function cancelWebOrderDropoff(params: {
  claimCode: string;
  email: string;
  appointmentId: string;
}): Promise<void> {
  await cancelWebOrderDropoffFn(params);
}

export async function answerWebOrderQuestion(params: {
  claimCode: string;
  email: string;
  text: string;
}): Promise<{ inquiryId: string }> {
  const result = await answerWebOrderQuestionFn(params);
  const data = result.data as { ok: boolean; inquiryId: string };
  return { inquiryId: data.inquiryId };
}

export async function submitWebOrderNote(params: {
  claimCode: string;
  email: string;
  text: string;
}): Promise<void> {
  await addWebOrderNoteFn(params);
}

export async function submitWebOrderPhoto(params: {
  claimCode: string;
  email: string;
  dataBase64: string;
  mimeType: string;
  fileName: string;
}): Promise<void> {
  await addWebOrderAttachmentFn(params);
}

export async function cancelBooking(params: {
  salonCode: string;
  appointmentId: string;
  token: string;
}): Promise<{ alreadyCanceled?: boolean }> {
  const result = await cancelWebBookingFn(params);
  return result.data as { ok: boolean; alreadyCanceled?: boolean };
}

const getRepairRequestFormFn = httpsCallable<{ salonCode: string }, RepairRequestFormData>(
  functions,
  "getRepairRequestForm"
);
const getRepairRequestDropoffAvailabilityFn = httpsCallable<
  { salonCode: string; date: string; employeeId?: string },
  DropoffAvailability
>(functions, "getRepairRequestDropoffAvailability");
const submitRepairRequestFn = httpsCallable(functions, "submitRepairRequest");
const getRepairRequestCapacityDaysFn = httpsCallable(functions, "getRepairRequestCapacityDays");

export async function fetchRepairRequestForm(
  salonCode: string
): Promise<RepairRequestFormData> {
  const result = await getRepairRequestFormFn({ salonCode });
  return result.data;
}

export async function fetchRepairRequestDropoffAvailability(
  salonCode: string,
  date: string,
  employeeId?: string
): Promise<DropoffAvailability> {
  const result = await getRepairRequestDropoffAvailabilityFn({
    salonCode,
    date,
    employeeId,
  });
  return result.data;
}

export async function fetchRepairRequestCapacityDays(
  salonCode: string,
  serviceProductIds: string[],
  horizonDays = 21
): Promise<{ days: RepairCapacityDay[]; workMinutes: number }> {
  const result = await getRepairRequestCapacityDaysFn({
    salonCode,
    serviceProductIds,
    horizonDays,
  });
  return result.data as { days: RepairCapacityDay[]; workMinutes: number };
}

export async function submitRepairRequest(params: {
  salonCode: string;
  description: string;
  contact: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    address?: {
      street: string;
      postalCode: string;
      city: string;
    } | null;
  };
  assetRef?: {
    type: string;
    label: string;
    fields: Record<string, string>;
  };
  templateId?: string | null;
  dropoff?: {
    employeeId: string;
    startAt: string;
  };
  serviceProductIds?: string[];
  repairDay?: string;
  dropoffChoice?: string;
  photos?: Array<{ dataBase64: string; mimeType: string; fileName: string }>;
  clientRequestId?: string;
  website?: string;
  locale?: string;
}): Promise<{
  orderId: string;
  orderNumber: string;
  claimCode: string | null;
  emailSent: boolean;
  repairDropoff?: { date: string; from: string; to: string } | null;
}> {
  const guestName = [params.contact.firstName, params.contact.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  const result = await submitRepairRequestFn({
    ...params,
    guestName,
    guestEmail: params.contact.email,
    guestPhone: params.contact.phone,
    fromApp: false,
  });
  const data = result.data as {
    ok: boolean;
    orderId: string;
    orderNumber: string;
    claimCode: string | null;
    emailSent: boolean;
    repairDropoff?: { date: string; from: string; to: string } | null;
  };
  return {
    orderId: data.orderId,
    orderNumber: data.orderNumber,
    claimCode: data.claimCode,
    emailSent: data.emailSent,
    repairDropoff: data.repairDropoff ?? null,
  };
}

export type WarmableCallable =
  | "submitRepairRequest"
  | "getRepairRequestCapacityDays"
  | "getRepairRequestDropoffAvailability"
  | "getWebOrderPickupAvailability"
  | "scheduleWebOrderPickup"
  | "getWebOrderDropoffAvailability"
  | "scheduleWebOrderDropoff"
  | "addWebOrderNote"
  | "addWebOrderAttachment"
  | "answerWebOrderQuestion"
  | "createWebBooking";

const warmedCallables = new Set<WarmableCallable>();

/**
 * Fire-and-forget ping so the real call a step later skips the cold start.
 * Each callable is pinged at most once per page load.
 */
export function warmup(...names: WarmableCallable[]): void {
  for (const name of names) {
    if (warmedCallables.has(name)) continue;
    warmedCallables.add(name);
    void httpsCallable(functions, name)({ warmup: true }).catch(() => undefined);
  }
}

export function formatFirebaseError(error: unknown): string {
  if (error && typeof error === "object" && "message" in error) {
    return String((error as { message: string }).message);
  }
  return "Ein unerwarteter Fehler ist aufgetreten.";
}
